<template>
    <!-- This example requires Tailwind CSS v2.0+ -->
    <div class="relative z-20" aria-labelledby="modal-title" role="dialog" aria-modal="true" v-if="visible" @keyup.esc="hide">
        <!--
          Background backdrop, show/hide based on modal state.

          Entering: "ease-out duration-300"
            From: "opacity-0"
            To: "opacity-100"
          Leaving: "ease-in duration-200"
            From: "opacity-100"
            To: "opacity-0"
        -->
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

        <div class="fixed z-20 inset-0 overflow-y-auto">
            <div class="flex items-end sm:items-center justify-center min-h-full p-4 text-center sm:p-0">
                <!--
                  Modal panel, show/hide based on modal state.

                  Entering: "ease-out duration-300"
                    From: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    To: "opacity-100 translate-y-0 sm:scale-100"
                  Leaving: "ease-in duration-200"
                    From: "opacity-100 translate-y-0 sm:scale-100"
                    To: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                -->
                <div
                    class="relative bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:w-full sm:p-6"
                    :class="{'lg:w-6/12 xl:w-5/12 md:w-3/5 sm:w-11/12 xs:w-full' : !showComparison, 'lg:w-11/12 xl:w-4/5': showComparison}"
                >
                    <div class="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                        <button type="button" class="btn-close" @click="cancel">
                            <span class="sr-only">{{ $t('shared.modals.close') }}</span>
                            <icon name="close"/>
                        </button>
                    </div>
                    <div class="sm:flex sm:items-star w-full" v-if="!showComparison">
                        <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                            <h3 class="text-xl leading-6 font-medium">{{$t('shared.modals.adjust_max_price_modal.title')}}</h3>
                            <div class="mt-2 w-full py-5">
                                <form ref="adjustForm">
                                    <div class="flex w-full content-between relative">
                                        <label for="maxPrice" class="w-1/2 ">{{$t('shared.modals.adjust_max_price_modal.max_price_label')}}</label>
                                        <input ref="maxPrice"
                                               type="number"
                                               name="maxPrice"
                                               id="maxPrice"
                                               class="w-1/2"
                                               :class="{'error' : !isMaxPriceValid()}"
                                               :placeholder="form.maxPrice"
                                               v-model="form.maxPrice"
                                               :min="minPrice"
                                               @keyup="checkMaxPrice">
                                        <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                            <icon class="h-5 w-5 text-red-500" name="exclamation" v-if="!isMaxPriceValid()"/>
                                        </div>
                                    </div>
                                </form>
                            </div>
                            <alert-message
                                v-if="errors && errors.length > 0"
                                :error-type="ERROR_TYPE_ERROR"
                                :messages="errors"
                                :display-errors-title="false"
                                class="mb-5"
                            />
                        </div>
                    </div>
                    <div class="sm:flex sm:items-star w-full" v-if="showComparison">
                        <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                            <h2 class="text-lg leading-6 font-bold text-gray-900">{{$t('shared.modals.adjust_max_price_modal.title')}}</h2>
                            <div class="mt-10 w-full flex">
                                <div class="pr-2" ref="titleBefore">
                                    <max-price-adjustment-overview
                                        :headline="this.$t('shared.modals.adjust_max_price_modal.adjust_overview.before_adjustment')"
                                        :maxPrice="this.currentMaxPrice"
                                    />
                                </div>
                                <div ref="titleAfter">
                                    <max-price-adjustment-overview
                                        :headline="this.$t('shared.modals.adjust_max_price_modal.adjust_overview.after_adjustment')"
                                        :bold-headline="true"
                                        :maxPrice="newMaxPrice"
                                    />
                                </div>
                            </div>
                            <div class="w-full">
                                <pyramid-levels-before-and-after
                                    :levels="this.filteredLevels"
                                    :forceHeaderSecondLine="true"
                                    ref="beforeAndAfter"
                                    @updated="resizeHandler"
                                />
                            </div>
                        </div>
                    </div>
                    <warning class="mt-10 text-yellow-500 bg-orange-100" :text="this.$t('shared.modals.adjust_quantity_modal.warning_adjust')" v-if="showComparison && isAdjustmentNeeded()"/>
                    <warning class="mt-10 text-gray-500 bg-gray-100" :text="this.$t('shared.modals.warning')" v-if="showComparison"/>
                    <div class="mt-5 sm:mt-4 flex flex-row-reverse">
                        <button v-if="showComparison" type="button" class="btn btn-primary ml-5" @click="confirm">{{$t('shared.modals.confirm')}}</button>
                        <button v-if="!showComparison" type="button" class="btn btn-primary ml-5" @click="preview">{{$t('shared.modals.preview')}}</button>
                        <button v-if="showComparison" type="button" class="btn btn-secondary ml-5" @click="adjustMaxPrice">{{$t('shared.modals.adjust')}}</button>
                        <button type="button" class="btn btn-secondary" @click="cancel">{{$t('shared.modals.cancel')}}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>


<script>
import Layout from '@/Shared/Layout';
import Icon from '@/Shared/Icon';
import {ErrorTypes} from '../../mixins/ErrorTypes';
import AlertMessage from "@/Shared/Alerts/AlertMessage";
import MaxPriceAdjustmentOverview from "../Overviews/MaxPriceAdjustmentOverview";
import Warning from "../Alerts/Warning";
import PyramidLevelsBeforeAndAfter from "../Tables/PyramidLevelsBeforeAndAfter";


export default {
    layout: Layout,
    components: {
        MaxPriceAdjustmentOverview,
        PyramidLevelsBeforeAndAfter,
        Icon,
        AlertMessage,
        Warning
    },
    mixins:[
        ErrorTypes
    ],
    props: {
        currentMaxPrice: {
            Type: Number,
            default: 0
        },
        minPrice: Number,
        auction: Object,
        activeLevel: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            visible: false,
            errors: [],
            showComparison: false,
            newMaxPrice: this.currentMaxPrice,

            newLevels: [],
            oldLevels: [],
            oldPlan: 200,
            oldSold: 76,
            oldAvailable: 124,

            newPlan: 200,
            newSold: 76,
            newAvailable: 124,
            form: {
                maxPrice: 0.00
            }
        }
    },
    methods: {
        resizeHandler() {
            if (this.$refs.titleBefore) {
                if (this.$refs.beforeAndAfter) {
                    let dimensions = this.$refs.beforeAndAfter.getDimensions();
                    this.$refs.titleBefore.style.width = dimensions.left + 'px';
                    this.$refs.titleBefore.style.marginRight = dimensions.mid + 'px';
                    this.$refs.titleAfter.style.width =dimensions.right + 'px';
                }
            }
        },
        show() {
            this.visible = true;
            this.errors = [];
            this.form.maxPrice = this.currentMaxPrice;
            this.$nextTick(() => this.$refs.maxPrice?.focus());
        },
        hide() {
            this.visible = false;
            this.showComparison = false;
        },
        cancel() {
            this.hide();
            this.$emit('cancel');
            this.showComparison = false;
        },
        adjustMaxPrice() {
            if (true === this.showComparison) {
                this.showComparison = false;
                this.$nextTick(() => this.$refs.maxPrice?.focus());
            }
        },
        preview() {
            let app = this;
            if (this.checkForm()) {
                this.newMaxPrice = parseFloat(this.$refs.maxPrice?.value);
                axios.post(route('auctions.adjust.maxPrice', { auction: this.auction.id, newMaxPrice: this.$refs.maxPrice?.value, confirmed: 0 }))
                    .then((response) => {
                        app.oldLevels = response.data.data.preview.old;
                        app.newLevels = response.data.data.preview.new;
                        app.showComparison = true;
                    })
                    .catch((error) => {
                        console.error(error);
                    });
            }
        },
        confirm() {
            let app = this;
            axios.post(route('auctions.adjust.maxPrice', { auction: this.auction.id, newMaxPrice: this.newMaxPrice, confirmed: 1 }))
                .then((response) => {
                    app.$emit('accept');
                    app.showComparison = false;
                    app.errors = [];
                    app.visible = false;
                    app.$inertia.reload();
                })
                .catch((error) => {
                    console.error(error);
                });
        },
        checkForm: function () {
            this.errors = this.checkMaxPrice();
            return this.errors.length === 0;
        },
        checkMaxPrice() {
            this.errors = [];
            let ref = this.$refs.maxPrice;
            if(ref) {
                let maxPrice = ref.value;
                if (maxPrice.length < 1) {
                    this.errors.push(this.$t('shared.modals.adjust_max_price_modal.max_price_errors.required'));
                }

                if (maxPrice.length > 0 && isNaN(parseFloat(maxPrice))) {
                    this.errors.push(this.$t('shared.modals.adjust_max_price_modal.max_price_errors.number'));
                }

                if (parseFloat(maxPrice) < this.minPrice) {
                    this.errors.push(this.$t('shared.modals.adjust_max_price_modal.max_price_errors.too_low', {minPrice: this.$options.filters.toCurrency(this.minPrice) }));
                }
            }
            return this.errors;
        },
        isMaxPriceValid() {
            return this.errors?.length === 0 ;
        },
        isAdjustmentNeeded() {
            let retVal = false;
            if (this.newLevels) {
                this.newLevels.forEach((level) => {
                    if (level.adjusted === true) {
                        retVal = true;
                    }
                });
            }
            return retVal;
        }
    },
    computed: {
        filteredLevels() {
            let self = this;
            let retVal = [];
            let allowedStates = ['future'];
            let oldLevels = this.oldLevels.filter(lvl => allowedStates.includes(lvl.state) && lvl.sequence >= self.activeLevel.sequence);
            let newLevels = this.newLevels.filter(lvl => allowedStates.includes(lvl.state) && lvl.sequence >= self.activeLevel.sequence);
            let lvlCount = Math.max(oldLevels.length, newLevels.length);
            for( let i = 0; i < lvlCount; i++) {
                let lvl = {};
                // if old level exists
                if (oldLevels[i]) {
                    lvl.sequence = oldLevels[i].sequence;
                    lvl.price_drop = oldLevels[i].price_drop;
                    lvl.fixed_time = oldLevels[i].fixed_time;
                    lvl.price = oldLevels[i].price;
                    lvl.quantity = oldLevels[i].quantity;
                } else {
                    lvl.sequence = '';
                    lvl.price_drop = 0;
                    lvl.fixed_time = '';
                    lvl.price = null;
                    lvl.quantity = '';
                }

                // if new level exists
                if (newLevels[i]) {
                    lvl.new_sequence = newLevels[i].sequence;
                    lvl.new_price_drop = newLevels[i].price_drop;
                    lvl.new_fixed_time = newLevels[i].fixed_time;
                    lvl.new_price = newLevels[i].price;
                    lvl.new_quantity = newLevels[i].quantity;
                    lvl.adjusted = newLevels[i].adjusted;
                } else {
                    lvl.new_sequence = '';
                    lvl.new_price_drop = 0;
                    lvl.new_fixed_time = '';
                    lvl.new_price = null;
                    lvl.new_quantity = '';
                    lvl.adjusted = false;
                }
                retVal.push(lvl);
            }
            return retVal;
        },
    }
}
</script>
